/**
 * Liquid metal WebGL renderer for ENRAGE menu labels.
 *
 * Direct port of the rareui liquid-metal component shader
 * (github.com/Codewithswappy/RareUI, MIT) with ONE minimal change:
 *
 *   The upstream shader does `uv.x *= u_ratio;` so every term that
 *   depends on x grows linearly with canvas aspect. On wide menu
 *   labels (250x36 -> aspect ~6.9) the `diagonal`, `dist`, and
 *   `bulge` terms explode on the right side and rightmost letters
 *   smudge into chromatic noise. We clamp the effective ratio used
 *   for that scaling to a max of ~2.5 (which is what rareui's
 *   default SVG geometry produces for their RareUI demo). Stripes
 *   still drift left->right but the math no longer blows up.
 *
 * Everything else - Poisson distance-field mask, stripe widths,
 * tone curve, refraction, glyph thickening via stroke - is from
 * rareui upstream.
 */

(function () {
    'use strict';

    if (typeof window === 'undefined') return;

    /* Defaults are rareui's own defaults. Theme settings override
       them at runtime via window.LIQUID_METAL_PARAMS. */
    const DEFAULTS = {
        speed: 0.3,
        dispersion: 0.005,
        edge: 0.5,
        patternBlur: 0.005,
        liquify: 0.08,
        patternScale: 2,
        strokePx: 4,
    };
    const OVERRIDES = (window.LIQUID_METAL_PARAMS || {});
    const PARAMS = Object.assign({}, DEFAULTS, OVERRIDES);
    const MASK_SUPERSAMPLE = 3;
    const POISSON_ITERS = 150;

    const VERT = `#version 300 es
        precision mediump float;
        in vec2 a_position;
        out vec2 vUv;
        void main() {
            vUv = .5 * (a_position + 1.);
            gl_Position = vec4(a_position, 0.0, 1.0);
        }
    `;

    const FRAG = `#version 300 es
        #ifdef GL_ES
        precision highp float;
        #else
        precision mediump float;
        #endif

        in vec2 vUv;
        out vec4 fragColor;

        uniform sampler2D u_image_texture;
        uniform float u_time;
        uniform float u_ratio;       /* true canvas aspect, used for image fit */
        uniform float u_ratio_clamp; /* clamped aspect used for shader math */
        uniform float u_img_ratio;
        uniform float u_patternScale;
        uniform float u_refraction;
        uniform float u_edge;
        uniform float u_patternBlur;
        uniform float u_liquid;

        #define PI 3.14159265358979323846

        vec3 mod289(vec3 x) { return x - floor(x * (1. / 289.)) * 289.; }
        vec2 mod289(vec2 x) { return x - floor(x * (1. / 289.)) * 289.; }
        vec3 permute(vec3 x) { return mod289(((x*34.)+1.)*x); }
        float snoise(vec2 v) {
            const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
            vec2 i = floor(v + dot(v, C.yy));
            vec2 x0 = v - i + dot(i, C.xx);
            vec2 i1;
            i1 = (x0.x > x0.y) ? vec2(1., 0.) : vec2(0., 1.);
            vec4 x12 = x0.xyxy + C.xxzz;
            x12.xy -= i1;
            i = mod289(i);
            vec3 p = permute(permute(i.y + vec3(0., i1.y, 1.)) + i.x + vec3(0., i1.x, 1.));
            vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.);
            m = m*m; m = m*m;
            vec3 x = 2. * fract(p * C.www) - 1.;
            vec3 h = abs(x) - 0.5;
            vec3 ox = floor(x + 0.5);
            vec3 a0 = x - ox;
            m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
            vec3 g;
            g.x = a0.x * x0.x + h.x * x0.y;
            g.yz = a0.yz * x12.xz + h.yz * x12.yw;
            return 130. * dot(m, g);
        }

        vec2 get_img_uv() {
            vec2 img_uv = vUv;
            img_uv -= .5;
            if (u_ratio > u_img_ratio) {
                img_uv.x = img_uv.x * u_ratio / u_img_ratio;
            } else {
                img_uv.y = img_uv.y * u_img_ratio / u_ratio;
            }
            img_uv += .5;
            img_uv.y = 1. - img_uv.y;
            return img_uv;
        }

        vec2 rotate(vec2 uv, float th) {
            return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
        }

        float get_color_channel(float c1, float c2, float stripe_p, vec3 w, float extra_blur, float b) {
            float ch = c2;
            float border = 0.;
            float blur = u_patternBlur + extra_blur;

            ch = mix(ch, c1, smoothstep(.0, blur, stripe_p));

            border = w[0];
            ch = mix(ch, c2, smoothstep(border - blur, border + blur, stripe_p));

            b = smoothstep(.2, .8, b);
            border = w[0] + .4 * (1. - b) * w[1];
            ch = mix(ch, c1, smoothstep(border - blur, border + blur, stripe_p));

            border = w[0] + .5 * (1. - b) * w[1];
            ch = mix(ch, c2, smoothstep(border - blur, border + blur, stripe_p));

            border = w[0] + w[1];
            ch = mix(ch, c1, smoothstep(border - blur, border + blur, stripe_p));

            float gradient_t = (stripe_p - w[0] - w[1]) / w[2];
            float gradient = mix(c1, c2, smoothstep(0., 1., gradient_t));
            ch = mix(ch, gradient, smoothstep(border - blur, border + blur, stripe_p));

            return ch;
        }

        float get_img_frame_alpha(vec2 uv, float img_frame_width) {
            float img_frame_alpha = smoothstep(0., img_frame_width, uv.x) * smoothstep(1., 1. - img_frame_width, uv.x);
            img_frame_alpha *= smoothstep(0., img_frame_width, uv.y) * smoothstep(1., 1. - img_frame_width, uv.y);
            return img_frame_alpha;
        }

        void main() {
            vec2 uv = vUv;
            uv.y = 1. - uv.y;
            uv.x *= u_ratio_clamp; /* CHANGED FROM rareui: clamped aspect for shader math */

            float diagonal = uv.x - uv.y;
            float t = .001 * mod(u_time, 10000.0);

            vec2 img_uv = get_img_uv();
            vec4 img = texture(u_image_texture, img_uv);

            vec3 color = vec3(0.);
            float opacity = 1.;

            vec3 color1 = vec3(.98, 0.98, 1.);
            vec3 color2 = vec3(.1, .1, .1);

            float edge = img.r;

            vec2 grad_uv = uv;
            grad_uv -= .5;

            float dist = length(grad_uv + vec2(0., .2 * diagonal));
            grad_uv = rotate(grad_uv, (.25 - .2 * diagonal) * PI);

            float bulge = pow(1.8 * dist, 1.2);
            bulge = 1. - bulge;
            bulge *= pow(uv.y, .3);

            float cycle_width = u_patternScale;
            float thin_strip_1_ratio = .12 / cycle_width * (1. - .4 * bulge);
            float thin_strip_2_ratio = .07 / cycle_width * (1. + .4 * bulge);
            float wide_strip_ratio = (1. - thin_strip_1_ratio - thin_strip_2_ratio);

            float thin_strip_1_width = cycle_width * thin_strip_1_ratio;
            float thin_strip_2_width = cycle_width * thin_strip_2_ratio;

            opacity = 1. - smoothstep(.9 - .5 * u_edge, 1. - .5 * u_edge, edge);
            opacity *= get_img_frame_alpha(img_uv, 0.01);

            float noise = snoise(uv - t);
            edge += (1. - edge) * u_liquid * noise;

            float refr = 0.;
            refr += (1. - bulge);
            refr = clamp(refr, 0., 1.);

            float dir = grad_uv.x;
            dir += diagonal;
            dir -= 2. * noise * diagonal * (smoothstep(0., 1., edge) * smoothstep(1., 0., edge));

            bulge *= clamp(pow(uv.y, .1), .3, 1.);
            dir *= (.1 + (1.1 - edge) * bulge);
            dir *= smoothstep(1., .7, edge);
            dir += .18 * (smoothstep(.1, .2, uv.y) * smoothstep(.4, .2, uv.y));
            dir += .03 * (smoothstep(.1, .2, 1. - uv.y) * smoothstep(.4, .2, 1. - uv.y));
            dir *= (.5 + .5 * pow(uv.y, 2.));
            dir *= cycle_width;
            dir -= t;

            float refr_r = refr;
            refr_r += .03 * bulge * noise;
            float refr_b = 1.3 * refr;

            refr_r += 5. * (smoothstep(-.1, .2, uv.y) * smoothstep(.5, .1, uv.y)) * (smoothstep(.4, .6, bulge) * smoothstep(1., .4, bulge));
            refr_r -= diagonal;

            refr_b += (smoothstep(0., .4, uv.y) * smoothstep(.8, .1, uv.y)) * (smoothstep(.4, .6, bulge) * smoothstep(.8, .4, bulge));
            refr_b -= .2 * edge;

            refr_r *= u_refraction;
            refr_b *= u_refraction;

            vec3 w = vec3(thin_strip_1_width, thin_strip_2_width, wide_strip_ratio);
            w[1] -= .02 * smoothstep(.0, 1., edge + bulge);
            /* CHANGED FROM rareui: unified extra_blur for all three RGB
               channels. Upstream uses different blurs per channel
               (R: 0.02+dispersion, G: 0.01/(1.-diagonal), B: 0.01) which
               produces position-dependent colour fringing on the right
               side of wide labels - that's the residual purple even
               when dispersion = 0. With a single blur, the only source
               of chromatic split is u_refraction itself, so setting
               dispersion to 0 yields pure mono chrome. */
            float channel_blur = 0.02 + .03 * u_refraction * bulge;
            float stripe_r = mod(dir + refr_r, 1.);
            float r = get_color_channel(color1.r, color2.r, stripe_r, w, channel_blur, bulge);
            float stripe_g = mod(dir, 1.);
            float g = get_color_channel(color1.g, color2.g, stripe_g, w, channel_blur, bulge);
            float stripe_b = mod(dir - refr_b, 1.);
            float b = get_color_channel(color1.b, color2.b, stripe_b, w, channel_blur, bulge);

            color = vec3(r, g, b);
            color *= opacity;

            fragColor = vec4(color, opacity);
        }
    `;

    function compile(gl, type, src) {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
            console.warn('[liquid-metal] shader compile failed:', gl.getShaderInfoLog(s));
            gl.deleteShader(s);
            return null;
        }
        return s;
    }

    /**
     * Rasterise the text and build a Poisson distance-field mask -
     * pixels near letter edges stay near-white (255), pixels deep in
     * the glyph interior trend to black (0). Outside glyphs is pure
     * white. The shader inverts this: edge=1 -> opacity=0 (outside
     * the visible letter), edge low -> opacity=1 (inside).
     *
     * This is straight from rareui's parseLogoImage routine.
     */
    function buildPoissonMask(textEl, padCss, extraRightPad) {
        const cs = getComputedStyle(textEl);
        const fontPx = parseFloat(cs.fontSize);
        const rect = textEl.getBoundingClientRect();

        const scale = MASK_SUPERSAMPLE;
        const padX = padCss.x * scale;
        const padY = padCss.y * scale;
        const extraR = (extraRightPad || 0) * scale;
        // Canvas layout (matches resize() below):
        //   left:    -padCss.x   (one padX on the left only)
        //   top:     -padCss.y   (one padY above)
        //   width:   100% + padCss.x + extraRightPad
        //   height:  100% + 2 * padCss.y
        // The mask must use the exact same total dimensions so the
        // shader's image-fit logic doesn't stretch glyphs.
        const width = Math.max(8, Math.ceil(rect.width * scale + padX + extraR));
        const height = Math.max(8, Math.ceil(rect.height * scale + padY * 2));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return null;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        ctx.fillStyle = '#000000';
        ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${fontPx * scale}px ${cs.fontFamily}`;
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';
        if ('letterSpacing' in ctx) {
            const ls = parseFloat(cs.letterSpacing);
            if (!isNaN(ls)) ctx.letterSpacing = `${ls * scale}px`;
        }

        const strokePx = Math.max(0, (PARAMS.strokePx || 0)) * scale;
        if (strokePx > 0) {
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = strokePx;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.strokeText(textEl.textContent, padX, padY);
        }
        ctx.fillText(textEl.textContent, padX, padY);

        const src = ctx.getImageData(0, 0, width, height);
        const data = src.data;
        const N = width * height;

        const shape = new Uint8Array(N);
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = y * width + x;
                const p = i * 4;
                const r = data[p], g = data[p + 1], b = data[p + 2], a = data[p + 3];
                const isOutside = (r === 255 && g === 255 && b === 255 && a === 255) || a === 0;
                shape[i] = isOutside ? 0 : 1;
            }
        }

        const boundary = new Uint8Array(N);
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const idx = y * width + x;
                if (!shape[idx]) continue;
                let isB = false;
                for (let ny = y - 1; ny <= y + 1 && !isB; ny++) {
                    for (let nx = x - 1; nx <= x + 1 && !isB; nx++) {
                        if (nx < 0 || nx >= width || ny < 0 || ny >= height) { isB = true; break; }
                        if (!shape[ny * width + nx]) isB = true;
                    }
                }
                if (isB) boundary[idx] = 1;
            }
        }

        const u = new Float32Array(N);
        const v = new Float32Array(N);
        const C = 0.01;
        const ITERS = POISSON_ITERS;

        for (let iter = 0; iter < ITERS; iter++) {
            for (let y = 1; y < height - 1; y++) {
                for (let x = 1; x < width - 1; x++) {
                    const idx = y * width + x;
                    if (!shape[idx] || boundary[idx]) { v[idx] = 0; continue; }
                    const sum = u[idx + 1] + u[idx - 1] + u[idx + width] + u[idx - width];
                    v[idx] = (C + sum) * 0.25;
                }
            }
            u.set(v);
        }

        let maxVal = 0;
        for (let i = 0; i < N; i++) if (u[i] > maxVal) maxVal = u[i];
        if (maxVal === 0) maxVal = 1;

        const alpha = 2;
        const out = ctx.createImageData(width, height);
        for (let i = 0; i < N; i++) {
            const p = i * 4;
            if (!shape[i]) {
                out.data[p] = 255;
                out.data[p + 1] = 255;
                out.data[p + 2] = 255;
                out.data[p + 3] = 255;
            } else {
                const raw = u[i] / maxVal;
                const remapped = Math.pow(raw, alpha);
                const gray = Math.round(255 * (1 - remapped));
                out.data[p] = gray;
                out.data[p + 1] = gray;
                out.data[p + 2] = gray;
                out.data[p + 3] = 255;
            }
        }

        return { imageData: out, width, height };
    }

    /**
     * Public mount: split the text by whitespace and mount a separate
     * canvas + shader pipeline per word. Each word keeps an aspect
     * ratio close to rareui's RareUI demo (~2:1 - 3:1) so the
     * shader's `diagonal` / `dist` / `bulge` math behaves well across
     * every letter. With one canvas spanning a long phrase like
     * "Most Wanted", the rightmost letters end up at uv.x ~6+ where
     * stripe direction and refraction degrade.
     */
    function mount(textEl) {
        if (textEl.dataset.liquidMounted === '1') return;
        textEl.dataset.liquidMounted = '1';

        const text = textEl.textContent;
        if (!text || !text.trim()) {
            delete textEl.dataset.liquidMounted;
            return;
        }

        const parts = text.split(/(\s+)/);
        textEl.innerHTML = '';
        for (const p of parts) {
            if (p.length === 0) continue;
            if (/^\s+$/.test(p)) {
                textEl.appendChild(document.createTextNode(p));
            } else {
                const span = document.createElement('span');
                span.className = 'lm-word';
                span.textContent = p;
                textEl.appendChild(span);
            }
        }

        textEl.querySelectorAll('.lm-word').forEach(mountSingle);
    }

    function mountSingle(textEl) {
        if (textEl.dataset.liquidMounted === '1') return;
        textEl.dataset.liquidMounted = '1';

        const cs0 = getComputedStyle(textEl);
        const fontPx = parseFloat(cs0.fontSize);

        /* Tiny pad only for anti-aliasing fringe. Keep it small so
           the canvas doesn't grow into the bottom of the line above
           (was visible as a faint "cut" through the "New Arrivals"
           descender area). */
        const padCss = {
            x: 2,
            y: 2,
        };

        /* Extra padding on the right side of the canvas. The rareui
           shader's chromatic dispersion accumulates with uv.x - the
           strongest pink/cyan colour split lands at the right edge.
           By extending the canvas past the visible text we shift that
           coloured tail onto empty mask region (no glyphs there ->
           shader output opacity = 0 -> transparent). Visible letters
           only catch the gentle left-side gradient. */
        const extraRightPad = Math.ceil(fontPx * 0.8);

        const canvas = document.createElement('canvas');
        canvas.className = 'liquid-metal-canvas';
        canvas.setAttribute('aria-hidden', 'true');
        canvas.style.position = 'absolute';
        canvas.style.left = `-${padCss.x}px`;
        canvas.style.top = `-${padCss.y}px`;
        canvas.style.pointerEvents = 'none';
        /* width and height are set explicitly in resize() below.
           calc(100% + N) misbehaves for absolutely positioned canvas
           on Safari/iOS - 100% resolves to 0 (because containing
           block is inline shrink-to-fit), so the canvas display size
           collapses to N pixels and the pixel buffer renders into a
           squashed area, smudging glyphs. */
        textEl.appendChild(canvas);

        const gl = canvas.getContext('webgl2', {
            antialias: true,
            alpha: true,
            premultipliedAlpha: false,
        });

        if (!gl) {
            console.warn('[liquid-metal] WebGL2 not supported');
            canvas.remove();
            delete textEl.dataset.liquidMounted;
            return;
        }

        const vs = compile(gl, gl.VERTEX_SHADER, VERT);
        const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
        if (!vs || !fs) { canvas.remove(); return; }

        const program = gl.createProgram();
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.warn('[liquid-metal] program link failed:', gl.getProgramInfoLog(program));
            canvas.remove();
            return;
        }
        gl.useProgram(program);

        const uLocs = {};
        const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
        for (let i = 0; i < count; i++) {
            const info = gl.getActiveUniform(program, i);
            if (info) uLocs[info.name] = gl.getUniformLocation(program, info.name);
        }

        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
            gl.STATIC_DRAW
        );
        const posLoc = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(posLoc);
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        if (uLocs.u_patternScale) gl.uniform1f(uLocs.u_patternScale, PARAMS.patternScale);
        if (uLocs.u_refraction)   gl.uniform1f(uLocs.u_refraction, PARAMS.dispersion);
        if (uLocs.u_edge)         gl.uniform1f(uLocs.u_edge, PARAMS.edge);
        if (uLocs.u_patternBlur)  gl.uniform1f(uLocs.u_patternBlur, PARAMS.patternBlur);
        if (uLocs.u_liquid)       gl.uniform1f(uLocs.u_liquid, PARAMS.liquify);

        let texture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        if (uLocs.u_image_texture) gl.uniform1i(uLocs.u_image_texture, 0);

        let imgW = 0, imgH = 0;
        function rebuildMask() {
            const result = buildPoissonMask(textEl, padCss, extraRightPad);
            if (!result) return false;
            const { imageData, width, height } = result;
            imgW = width;
            imgH = height;
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0,
                gl.RGBA, gl.UNSIGNED_BYTE, imageData.data);
            return true;
        }

        /* Maximum aspect we let the shader math see. Lower cap keeps
           the diagonal / dispersion gradient gentler across long
           words like "Wanted". 2.0 is a sweet spot - the chrome
           pattern stays even from first letter to last. */
        const SHADER_RATIO_CAP = 2.0;

        let canvasW = 0, canvasH = 0;
        function resize() {
            const rect = textEl.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const cssW = rect.width + padCss.x + extraRightPad;
            const cssH = rect.height + padCss.y * 2;
            /* Set both the CSS display size and the pixel buffer.
               CSS size explicit (not via calc) so Safari can't
               collapse the inline-block containing block. Pixel
               buffer = CSS * DPR for sharp rendering. */
            canvas.style.width = cssW + 'px';
            canvas.style.height = cssH + 'px';
            const w = Math.max(1, Math.ceil(cssW * dpr));
            const h = Math.max(1, Math.ceil(cssH * dpr));
            if (w === canvasW && h === canvasH) return;
            canvas.width = w;
            canvas.height = h;
            canvasW = w;
            canvasH = h;
            gl.viewport(0, 0, w, h);
            const trueRatio = w / h;
            if (uLocs.u_ratio) gl.uniform1f(uLocs.u_ratio, trueRatio);
            if (uLocs.u_ratio_clamp) gl.uniform1f(uLocs.u_ratio_clamp, Math.min(trueRatio, SHADER_RATIO_CAP));
            if (imgW && imgH && uLocs.u_img_ratio) gl.uniform1f(uLocs.u_img_ratio, imgW / imgH);
        }

        if (!rebuildMask()) { canvas.remove(); return; }
        resize();
        if (uLocs.u_img_ratio) gl.uniform1f(uLocs.u_img_ratio, imgW / imgH);

        let running = false;
        let rafId = null;
        let lastT = performance.now();
        let accumulated = 0;

        function loop(now) {
            const dt = now - lastT;
            lastT = now;
            accumulated += dt * PARAMS.speed;
            const normTime = accumulated % 10000;
            if (uLocs.u_time) gl.uniform1f(uLocs.u_time, normTime);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            if (running) rafId = requestAnimationFrame(loop);
        }

        function play() {
            if (running) return;
            running = true;
            lastT = performance.now();
            rafId = requestAnimationFrame(loop);
        }
        function pause() {
            running = false;
            if (rafId) cancelAnimationFrame(rafId);
            rafId = null;
        }

        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0] && entries[0].isIntersecting) play();
                else pause();
            },
            { threshold: 0.01 }
        );
        io.observe(textEl);

        const ro = new ResizeObserver(() => {
            resize();
            rebuildMask();
            if (uLocs.u_img_ratio) gl.uniform1f(uLocs.u_img_ratio, imgW / imgH);
        });
        ro.observe(textEl);

        const rect = textEl.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) play();
    }

    function init() {
        document.querySelectorAll('.drill-itemLabel.is-liquid .drill-itemText').forEach(mount);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    document.addEventListener('click', (e) => {
        const opener = e.target && e.target.closest && e.target.closest('[data-open="#menuDrawerBox"]');
        if (opener) setTimeout(init, 120);
    });
})();
